import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PageHeader from '../../components/layout/LayoutWrapper/PageHeader';
import { getAllCourseModules, updateCourse, getCourseDetails } from '../../utils/urls';
import axios from 'axios';
import CustomPaperBox from '../../components/common/CustomPaperBox';
import CustomButton from '../../components/common/CustomButton/CustomButton';
import ServerPaginationDataGridWithReactQuery from '../../components/common/CustomDataGrid/ServerPaginationDataGridWithReactQuery';
import CustomIconButton from '../../components/common/CustomButton copy/CustomIconButton';
import { Trash, Pencil } from "lucide-react";
import CustomSwitch from '../../components/common/CustomSwitch';
import EditCourse from './EditCourse';
import CustomDialog from '../../components/common/CustomDialog';
import EditModuleDialogContent from './EditModuleDialogContent';
import CustomModal from '../../components/common/CustomDialog/CustomModal';
import CustomChip from '../../components/common/CustomChip';

const ViewCourseDetails = () => {
    const { id } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [initialFormData, setInitialFormData] = useState(null);
    const [categories, setCategories] = useState([]);
    const [editModuleDialogOpen, setEditModuleDialogOpen] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [isAddMode, setIsAddMode] = useState(false);
    const [moduleTableKey, setModuleTableKey] = useState(0);

    const formikRef = useRef();
    const gridRef = useRef();

    useEffect(() => {
        const fetchCourseDetails = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(getCourseDetails.replace("{id}", id));
                const foundCourse = data?.data;

                setCourse(foundCourse);
                setInitialFormData({
                    id: foundCourse?.id,
                    title: foundCourse?.title,
                    description: foundCourse?.description,
                    category_id: foundCourse?.category_id,
                    banner_file: foundCourse?.course_banner_img_url,
                    access_for_all_user: foundCourse?.access_for_all_user,
                    access_for_verified_user: foundCourse?.access_for_verified_user,
                    availability_schedule: foundCourse?.availability_schedule,
                    schedule_date: foundCourse?.schedule_date,
                    sub_category_id: foundCourse?.sub_category,
                });

                const category = foundCourse?.category_id;
                setCategories(category ? [{
                    label: category.title,
                    value: category.id
                }] : []);
            } catch (error) {
                console.error("Error fetching course details", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCourseDetails();
    }, [id]);

    if (loading) return <div>Loading...</div>;
    if (!course || !initialFormData) return <div>No course found</div>;

    const handleEditDetails = async (updatedValues) => {
        try {
            await axios.put(`${updateCourse}/${course.id}`, updatedValues);
            gridRef.current?.restartComponent();
            console.log("Course updated successfully.");
        } catch (error) {
            console.error("Error updating course", error);
        }
    };

    const handleDeleteDetails = () => {
        console.log("Delete course details clicked");
    };

    const handleStatusChange = (newStatus, moduleId) => {
        console.log(`Module ID: ${moduleId}, New Status: ${newStatus}`);
    };

    const courseModulesColumns = (showStatus = false) => [
        { field: 'title', headerName: 'Module Title', flex: 1 },
        { field: 'file_type', headerName: 'File Type', flex: 1 },
        {
            field: 'url',
            headerName: 'File',
            width: 150,
            renderCell: (params) => {
                const { file_type, file_url, content, id } = params;
                const isExpanded = expandedRows[id] || false;

                if (file_type === "Text") {
                    return (
                        <div className="w-full">
                            <div className={`text-sm whitespace-pre-line ${isExpanded ? '' : 'line-clamp-1'} overflow-hidden`} style={{ maxWidth: '100%' }} title={!isExpanded ? content : undefined}>
                                {content || '-'}
                            </div>
                            {content && content.length > 100 && (
                                <button className="text-blue-600 text-xs underline mt-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedRows(prev => ({
                                            ...prev,
                                            [id]: !prev[id],
                                        }));
                                    }}>
                                    {isExpanded ? 'Read less' : 'Read more'}
                                </button>
                            )}
                        </div>
                    );
                }

                if (file_type === "URL") {
                    const urlToOpen = file_url || content;
                    if (urlToOpen) {
                        return (
                            <a href={urlToOpen} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                                [Open]
                            </a>
                        );
                    }
                    return "-";
                }


                if (["PDF", "Video"].includes(file_type) && file_url) {
                    return (
                        <a href={file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                            [Open]
                        </a>
                    );
                }

                return "-";
            },
        },
        ...(showStatus ? [{
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <div className="flex items-center gap-2">
                    <CustomSwitch
                        value={params.status}
                        onChange={(newStatus) => handleStatusChange(newStatus, params.id)}
                        name={`status-${params.id}`}
                    />
                    <span>{params.status ? "Enable" : "Disable"}</span>
                </div>
            ),
        }] : []),
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            renderCell: (params) => (
                <div className="flex gap-2">
                    <CustomIconButton
                        icon={<Pencil className="text-green-600" size={20} />}
                        onClick={() => {
                            setEditingModule(params);
                            setEditModuleDialogOpen(true);
                        }}
                    />
                    <CustomIconButton
                        icon={<Trash className="text-red-600" size={20} />}
                        onClick={() => handleDeleteDetails()}
                    />
                </div>
            ),
        },
    ];


    return (
        <div>
            <PageHeader title={course.title} size="md" showBack={true} />

            <CustomPaperBox variant="none" elevation="2" className="course-details-box">
                {/* <div className="mb-4 flex justify-end">
                    <CustomButton label="Edit Details" onClick={() => setEditDialogOpen(true)} variant="outlined" size="sm" />
                </div> */}
                <div className="flex gap-4 items-start">
                    <div className="w-2/5">
                        <div className="w-full aspect-video rounded-md overflow-hidden">
                            <img src={course.course_banner_img_url} alt={course.title} className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="w-3/5">
                        <CustomChip
                            label={course.category_id?.title || "No Category"}
                            variant="blue"
                            size="md"
                        />
                        <div className="course-description mt-2">
                            <strong className="text-gray-700">Description:</strong>
                            <p>{course.description}</p>
                        </div>
                    </div>
                </div>
            </CustomPaperBox>
            <div className='my-5 flex justify-end'>

                <CustomButton
                    label="Add Module"
                    variant="contained"
                    size="lg"
                    onClick={() => {
                        setEditingModule(null);
                        setIsAddMode(true);
                        setEditModuleDialogOpen(true);
                    }}
                />
            </div>

            <ServerPaginationDataGridWithReactQuery
                key={moduleTableKey}
                url={getAllCourseModules.replace("{course_id}", course.id)}
                limit={10}
                columns={courseModulesColumns(false)}
                keyId="id"
                isServerPagination={true}
                ref={gridRef}
                dataMapper={(data) => {
                    return data.map(module => ({
                        id: module.id,
                        course_id: module.course_id,
                        title: module.title,
                        content: module.content,
                        file_url: module.file_url,
                        file_type: module.file_type,
                        created_at: module.created_at,
                        status: module.status,
                    }));
                }}
            />

            <CustomDialog
                open={editDialogOpen}
                setOpen={setEditDialogOpen}
                buttonComp={() => null}
                width="900px"
                title="Edit Course Details"
                showClose={true}
                showBack={false}
                onClose={() => setEditDialogOpen(false)}
            >
                <EditCourse courseData={initialFormData} onSubmit={handleEditDetails} />
            </CustomDialog>

            <CustomModal
                open={editModuleDialogOpen}
                title={editingModule ? "Edit Module" : "Add Module"}
                onCancel={() => {
                    setEditModuleDialogOpen(false);
                    setEditingModule(null);
                }}
                onSubmit={() => {
                    formikRef.current?.submitForm();
                }}
                submitLabel={editingModule ? "Update Module" : "Create Module"}
                cancelLabel="Cancel"
            >
                <EditModuleDialogContent
                    formikRef={formikRef}
                    module={editingModule}
                    courseId={course.id}
                    isAddMode={isAddMode}
                    editingModule={editingModule}
                    onClose={() => {
                        setEditModuleDialogOpen(false);
                        setEditingModule(null);
                    }}
                    onUpdateModule={() => {
                        gridRef.current?.restartComponent();
                        setModuleTableKey(prev => prev + 1);
                    }}
                />
            </CustomModal>
        </div>
    );
};

export default ViewCourseDetails;
